import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import RestoreIcon from '@mui/icons-material/Restore';
import TableChartIcon from '@mui/icons-material/TableChart';
import ViewListIcon from '@mui/icons-material/ViewList';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFlag } from '@unleash/proxy-client-react';
import type { TFunction } from 'i18next';
import { useSetAtom } from 'jotai';
import { type Dispatch, type ReactNode, type SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type Location, useLocation, useNavigate } from 'react-router';
import { versionAtom } from '@/atoms/version';
import { DateTypography } from '@/components/DateTypography/DateTypography';
import { ExportScript } from '@/components/ExportScript/ExportScript';
import { Hint } from '@/components/Hint/Hint';
import { useHints } from '@/components/Hint/useHints';
import { LogoSvgNew } from '@/components/Icons/LogoSvgNew';
import { SmallLogoSvgNew } from '@/components/Icons/SmallLogoSvgNew';
import { StylusSvg } from '@/components/Icons/StylusSvg';
import { ImportScript } from '@/components/ImportScript/ImportScript';
import { NavigationCompany } from '@/components/NavigationCompany/NavigationCompany';
import { NavigationProfile } from '@/components/NavigationProfile/NavigationProfile';
import { NavigationProject } from '@/components/NavigationProject/NavigationProject';
import { styles } from '@/components/StyledComponents';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { HINTS, paths } from '@/constants';
import { useProjectDetail } from '@/data/entities/projects';
import { useSnapshotsList } from '@/data/entities/snapshots';
import { hintsBlock, snackbarColor } from '@/enums';
import { useColorMode, useProjectId, useSnackbar, useVersion } from '@/hooks';
import { useEditor } from '@/hooks/useEditor';
import { useChangeUser, usePrefValue, useSetPref } from '@/preferences';
import { useTutorialEnabled } from '@/tutorial/tutorialGate';
import { isMovieProject } from '@/utils/projectHelpers';
import { SideBarContainer, SideBarGrid } from './Navigation.styles';
import { NavigationLink } from './NavigationLink';

/** «Помощь» opens the same items wherever it sits in the menu. The tutorial's
 *  is behind its flag. */
const helpLinks = (t: TFunction, tutorialOn: boolean) => [
    ...(tutorialOn ? [{ to: paths.tutorial.index, title: t('tutorial.editor-tutorial'), specialCondition: true }] : []),
    { to: paths.feedback.index, title: t('feedback'), specialCondition: true },
];

const NavigationWrap = ({
    children,
    isOpenLink,
    setOpenLink,
    showHelp = true,
}: {
    children?: ReactNode;
    isOpenLink: string;
    setOpenLink: Dispatch<SetStateAction<string>>;
    showHelp?: boolean;
}) => {
    const project_id = useProjectId();
    const { t } = useTranslation();
    const tutorialOn = useTutorialEnabled();
    return (
        <Grid
            container
            sx={{
                minHeight: '0',
                flexWrap: 'nowrap',
                flexDirection: 'column',
            }}
        >
            {project_id ? (
                <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].project} mode="menu">
                    <NavigationProject setOpen={setOpenLink} />
                </Hint>
            ) : null}
            <Grid
                container
                gap="4px"
                flexDirection="column"
                wrap="nowrap"
                sx={{
                    overflowY: 'scroll',
                    scrollbarWidth: 'none',
                    mt: '8px',
                    '::-webkit-scrollbar': {
                        width: '0',
                        height: '0',
                    },
                }}
            >
                {children}
                {showHelp && (
                    <Hint
                        block={hintsBlock.menu}
                        hint={HINTS[hintsBlock.menu].help}
                        anchorSx={{ alignItems: 'flex-start', pt: '2px' }}
                        mode="menu"
                    >
                        <NavigationLink
                            testId=""
                            to={'/'}
                            link={{
                                component: HelpOutlineIcon,
                                title: t('help'),
                                textColor: theme => theme.palette.primary.contrastText,
                                iconSize: '1.25rem',
                            }}
                            isOpen={isOpenLink === t('help')}
                            setOpen={setOpenLink}
                            children={helpLinks(t, tutorialOn)}
                        />
                    </Hint>
                )}
            </Grid>
        </Grid>
    );
};

export const Navigation = () => {
    const { t } = useTranslation();
    const isPro = useFlag('pro');
    const tutorialOn = useTutorialEnabled();
    const location = useLocation();
    const navigate = useNavigate();
    const showSnackbar = useSnackbar();
    const setEditorSidebar = useSetPref('editor_sidebar');
    const setVersionValue = useSetAtom(versionAtom);
    const side_bar = usePrefValue('side_bar');
    const plan_menu = usePrefValue('plan_menu');
    const changeUser = useChangeUser();
    const matches_1366 = useMediaQuery('(max-width:1366px)');
    const matches_1900 = useMediaQuery('(min-width:1900px)');
    const editorSideBar = usePrefValue('editor_sidebar');
    const showNavigator = usePrefValue('show_navigator');
    const isDarkMode = useColorMode();
    const theme = useTheme();
    const { compactMode } = useEditor();
    const version = useVersion();
    const projectId = useProjectId();
    const { data: project } = useProjectDetail({ projectId: projectId || 0, version: version || '' }, { enabled: !!projectId });
    const { data: snapshots } = useSnapshotsList({ projectId: projectId || 0 }, { enabled: !!projectId });
    const isFilmMode = isMovieProject(project);
    const [isHover, setHover] = useState(false);
    const [isOpenLink, setOpenLink] = useState('');
    const currentVersion = version && snapshots ? snapshots.find(item => item.tag === version) : null;

    const { displayHints } = useHints();

    useEffect(() => {
        if (compactMode) changeUser({ side_bar: !editorSideBar });
        if (matches_1900) changeUser({ side_bar: matches_1900 });
    }, [compactMode, matches_1900, editorSideBar, changeUser]);

    const getLinks = (location: Location, t: TFunction, isFilmMode: boolean, isPro: boolean) => {
        switch (location.pathname) {
            case paths.company.success:
            case paths.project.synopsis.index:
            case paths.project.synopsis.setting:
            case paths.project.synopsis.theme:
            case paths.project.synopsis.references:
            case paths.project.synopsis.marketing:
            case paths.project.synopsis.documents:
            case paths.project.plan.index:
            case paths.project.comments:
            case paths.project.index:
            case paths.project.line.index:
            case paths.project.lines.index:
            case paths.project.episode.index:
            case paths.project.scene.editor:
            case paths.project.scene.title:
            case paths.project.scene.editor_pep:
            case paths.concept.editor:
            case paths.project.versions:
            case paths.project.importReports:
            case paths.project.character.index:
            case paths.project.characters.index:
            case paths.project.episodes.index:
            case paths.project.episodes.synopsis:
            case paths.project.location.index:
            case paths.project.locations.index:
            case paths.project.devtools.fillProject:
            case paths.feedback.index:
            case paths.tutorial.index:
                // Film mode: new flat menu structure
                if (isFilmMode) {
                    return (
                        <NavigationWrap isOpenLink={isOpenLink} setOpenLink={setOpenLink} showHelp={false}>
                            {/* 1. Synopsis (direct link) */}
                            <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].synopsis} mode="menu">
                                <NavigationLink
                                    testId="NavigationLinkSynopsis"
                                    to={paths.project.synopsis.index}
                                    link={{
                                        component: StylusSvg,
                                        title: t('synopsis-page.synopsis'),
                                        textColor: theme => theme.palette.primary.contrastText,
                                        iconSize: '1.25rem',
                                    }}
                                    setOpen={setOpenLink}
                                    matchStartsWith
                                />
                            </Hint>

                            {/* 2. Storylines (direct link) */}
                            <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].lines} mode="menu">
                                <NavigationLink
                                    testId="NavigationLinkLines"
                                    to={paths.project.lines.index}
                                    link={{
                                        component: FormatListBulletedIcon,
                                        title: t('line.lines'),
                                        textColor: theme => theme.palette.primary.contrastText,
                                        iconSize: '1.25rem',
                                    }}
                                    setOpen={setOpenLink}
                                    matchStartsWith
                                />
                            </Hint>

                            <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].editor} mode="menu">
                                <NavigationLink
                                    testId="NavigationLinkTable"
                                    to={`${paths.project.scene.editor}?view=table`}
                                    link={{
                                        component: TableChartIcon,
                                        title: t('fountain.hierarchy.table-view'),
                                        textColor: theme => theme.palette.primary.contrastText,
                                        iconSize: '1.25rem',
                                    }}
                                    setOpen={setOpenLink}
                                    onClick={() => {
                                        matches_1366 &&
                                            !location.pathname.startsWith(paths.project.scene.editor) &&
                                            changeUser({ side_bar: false });
                                    }}
                                />
                            </Hint>

                            <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].plan} mode="menu">
                                <NavigationLink
                                    testId="NavigationLinkCards"
                                    to={`${paths.project.scene.editor}?view=cards`}
                                    link={{
                                        component: ArticleIcon,
                                        title: t('synopsis-page.cards'),
                                        textColor: theme => theme.palette.primary.contrastText,
                                        iconSize: '1.25rem',
                                    }}
                                    setOpen={setOpenLink}
                                    onClick={() => {
                                        matches_1366 &&
                                            !location.pathname.startsWith(paths.project.scene.editor) &&
                                            changeUser({ side_bar: false });
                                    }}
                                />
                            </Hint>

                            <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].editor} mode="menu">
                                <NavigationLink
                                    testId="NavigationLinkList"
                                    to={`${paths.project.scene.editor}?view=list`}
                                    link={{
                                        component: ViewListIcon,
                                        title: t('fountain.hierarchy.list-view'),
                                        textColor: theme => theme.palette.primary.contrastText,
                                        iconSize: '1.25rem',
                                    }}
                                    setOpen={setOpenLink}
                                    onClick={() => {
                                        matches_1366 &&
                                            !location.pathname.startsWith(paths.project.scene.editor) &&
                                            changeUser({ side_bar: false });
                                    }}
                                />
                            </Hint>

                            {/* 6. Characters (direct link) */}
                            <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].characters} mode="menu">
                                <NavigationLink
                                    testId="NavigationLinkCharacters"
                                    to={paths.project.characters.index}
                                    link={{
                                        component: PersonIcon,
                                        title: t('character.characters'),
                                        textColor: theme => theme.palette.primary.contrastText,
                                        iconSize: '1.25rem',
                                    }}
                                    setOpen={setOpenLink}
                                />
                            </Hint>

                            {/* 7. Locations/Objects (Pro only) */}
                            {isPro && (
                                <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].locations} mode="menu">
                                    <NavigationLink
                                        testId="NavigationLinkLocation"
                                        to={paths.project.locations.index}
                                        link={{
                                            component: LocationOnIcon,
                                            title: t('synopsis-page.locations'),
                                            textColor: theme => theme.palette.primary.contrastText,
                                            iconSize: '1.25rem',
                                        }}
                                        setOpen={setOpenLink}
                                    />
                                </Hint>
                            )}

                            {/* 8. Versions (direct link) */}
                            <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].versions} mode="menu">
                                <NavigationLink
                                    testId=""
                                    to={paths.project.versions}
                                    link={{
                                        component: RestoreIcon,
                                        title: t('versions'),
                                        textColor: theme => theme.palette.primary.contrastText,
                                        iconSize: '1.25rem',
                                    }}
                                    setOpen={setOpenLink}
                                />
                            </Hint>

                            {/* 9. Import reports (direct link) */}
                            <NavigationLink
                                testId=""
                                to={paths.project.importReports}
                                link={{
                                    component: DescriptionIcon,
                                    title: t('import-reports'),
                                    textColor: theme => theme.palette.primary.contrastText,
                                    iconSize: '1.25rem',
                                }}
                                setOpen={setOpenLink}
                            />
                        </NavigationWrap>
                    );
                }

                // TV mode: original menu structure
                return (
                    <NavigationWrap isOpenLink={isOpenLink} setOpenLink={setOpenLink}>
                        <Hint
                            block={hintsBlock.menu}
                            hint={HINTS[hintsBlock.menu].editor}
                            mode="menu"
                            anchorSx={{ alignItems: 'flex-start', pt: '2px' }}
                        >
                            <NavigationLink
                                testId=""
                                to={''}
                                link={{
                                    component: ArticleIcon,
                                    title: t('scenario'),
                                    textColor: theme => theme.palette.primary.contrastText,
                                    iconSize: '1.25rem',
                                }}
                                isOpen={isOpenLink === t('scenario')}
                                setOpen={setOpenLink}
                                children={[
                                    {
                                        to: paths.project.scene.title,
                                        title: t('title-list'),
                                        specialCondition: true,
                                    },
                                    {
                                        to: paths.project.scene.editor,
                                        title: t('scenario-in-dialogs'),
                                        specialCondition: true,
                                        onClick: () => {
                                            matches_1366 &&
                                                !location.pathname.startsWith(paths.project.scene.editor) &&
                                                changeUser({ side_bar: false });
                                        },
                                    },
                                ]}
                            />
                        </Hint>
                        <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].characters} mode="menu">
                            <NavigationLink
                                testId="NavigationLinkCharacters"
                                to={paths.project.characters.index}
                                link={{
                                    component: PersonIcon,
                                    title: t('character.characters'),
                                    textColor: theme => theme.palette.primary.contrastText,
                                    iconSize: '1.25rem',
                                }}
                                setOpen={setOpenLink}
                            />
                        </Hint>

                        <Hint
                            block={hintsBlock.menu}
                            hint={HINTS[hintsBlock.menu].synopsis}
                            mode="menu"
                            anchorSx={{ alignItems: 'flex-start', pt: '2px' }}
                        >
                            <NavigationLink
                                testId="NavigationLinkSynopsis"
                                to={''}
                                link={{
                                    component: StylusSvg,
                                    title: t('synopsis.synopsis'),
                                    textColor: theme => theme.palette.primary.contrastText,
                                    iconSize: '1.25rem',
                                }}
                                isOpen={isOpenLink === t('synopsis.synopsis')}
                                setOpen={setOpenLink}
                                children={[
                                    {
                                        to: paths.project.synopsis.index,
                                        title: t('episode.season-synopsis'),
                                        specialCondition: true,
                                    },
                                ]}
                            />
                        </Hint>

                        {isPro && (
                            <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].lines} mode="menu">
                                <NavigationLink
                                    testId="NavigationLinkLines"
                                    to={paths.project.lines.index}
                                    link={{
                                        component: FormatListBulletedIcon,
                                        title: t('line.lines'),
                                        textColor: theme => theme.palette.primary.contrastText,
                                        iconSize: '1.25rem',
                                    }}
                                    setOpen={setOpenLink}
                                />
                            </Hint>
                        )}

                        <Hint
                            block={hintsBlock.menu}
                            hint={HINTS[hintsBlock.menu].plan}
                            mode="menu"
                            anchorSx={{ alignItems: 'flex-start', pt: '2px' }}
                        >
                            <NavigationLink
                                testId=""
                                to={'/'}
                                link={{
                                    component: TableChartIcon,
                                    title: t('plan.season-plan'),
                                    textColor: theme => theme.palette.primary.contrastText,
                                    iconSize: '1.25rem',
                                }}
                                isOpen={isOpenLink === t('plan.season-plan')}
                                setOpen={setOpenLink}
                                children={[
                                    ...(isPro
                                        ? [
                                              {
                                                  to: paths.project.plan.index,
                                                  title: t('plan.sequences-by-series'),
                                                  onClick: () => plan_menu === '2' && changeUser({ plan_menu: '1' }),
                                                  specialCondition: plan_menu === '1',
                                              },
                                          ]
                                        : []),
                                    {
                                        to: paths.project.plan.index,
                                        title: t('navigation.episode-plan-season'),
                                        onClick: () => plan_menu === '1' && changeUser({ plan_menu: '2' }),
                                        specialCondition: plan_menu === '2',
                                    },
                                ]}
                            />
                        </Hint>

                        {isPro && (
                            <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].locations} mode="menu">
                                <NavigationLink
                                    testId="NavigationLinkLocation"
                                    to={paths.project.locations.index}
                                    link={{
                                        component: LocationOnIcon,
                                        title: t('locations.locations'),
                                        textColor: theme => theme.palette.primary.contrastText,
                                        iconSize: '1.25rem',
                                    }}
                                    setOpen={setOpenLink}
                                />
                            </Hint>
                        )}

                        <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].versions} mode="menu">
                            <NavigationLink
                                testId=""
                                to={paths.project.versions}
                                link={{
                                    component: RestoreIcon,
                                    title: t('versions'),
                                    textColor: theme => theme.palette.primary.contrastText,
                                    iconSize: '1.25rem',
                                }}
                                setOpen={setOpenLink}
                            />
                        </Hint>

                        <NavigationLink
                            testId=""
                            to={paths.project.importReports}
                            link={{
                                component: DescriptionIcon,
                                title: t('import-reports'),
                                textColor: theme => theme.palette.primary.contrastText,
                                iconSize: '1.25rem',
                            }}
                            setOpen={setOpenLink}
                        />
                    </NavigationWrap>
                );
            case paths.company.index:
            case paths.company.projects:
            case paths.company.projectsArchive:
            case paths.company.projectsDeleted:
            case paths.employees.index:
            case paths.employees.invite:
                return <NavigationWrap isOpenLink={isOpenLink} setOpenLink={setOpenLink} showHelp={!isFilmMode} />;
            // A route not listed above gets an empty sidebar (even the project
            // row goes): every new page needs its case here.
            default:
                return false;
        }
    };

    const links = getLinks(location, t, isFilmMode, isPro);
    // NavigationWrap lists «Помощь» itself outside Film mode; a page with no
    // menu (the profile) or a Film-mode menu gets it at the bottom instead.
    const menuShowsHelp = links !== false && !isFilmMode;

    return (
        <SideBarContainer onMouseEnter={() => displayHints(hintsBlock.menu)} collapsed={!side_bar}>
            <SideBarGrid container>
                <Grid container flexDirection="column" flexWrap="nowrap" sx={{ gap: '16px', minHeight: '0' }}>
                    <Box
                        sx={[
                            {
                                height: 'auto',
                                cursor: 'pointer',
                                display: 'flex',
                                '& svg': {
                                    fill: theme => (isDarkMode ? theme.custom.c11 : theme.custom.c24),
                                },
                                [theme.breakpoints.down('lg')]: {
                                    justifyContent: 'center',
                                    '& svg': {
                                        width: '156px',
                                        height: 'auto',
                                    },
                                },
                            },
                            !side_bar && {
                                marginLeft: '-10px',
                                [theme.breakpoints.down('lg')]: {
                                    justifyContent: 'flex-start',
                                    marginLeft: '0',
                                    '& svg': {
                                        width: '37px',
                                        height: 'auto',
                                    },
                                },
                            },
                        ]}
                        onClick={() => {
                            navigate(paths.company.index);
                            setVersionValue('');
                        }}
                    >
                        {side_bar ? <LogoSvgNew /> : <SmallLogoSvgNew />}
                    </Box>
                    <Grid
                        container
                        flexDirection="column"
                        flexWrap="nowrap"
                        sx={theme => ({
                            gap: '16px',
                            minHeight: '0',
                            [theme.breakpoints.down('lg')]: {
                                gap: '8px',
                            },
                        })}
                    >
                        <Grid
                            container
                            flexDirection="column"
                            flexWrap="nowrap"
                            sx={theme => ({
                                gap: '16px',
                                [theme.breakpoints.down('lg')]: {
                                    gap: '8px',
                                },
                            })}
                        >
                            <Grid
                                container
                                gap="8px"
                                alignItems="center"
                                padding="6px 0 6px 8px"
                                position="relative"
                                flexWrap="nowrap"
                                onMouseEnter={() => setHover(true)}
                                onMouseLeave={() => setHover(false)}
                                onClick={() => {
                                    compactMode && !side_bar && showNavigator && setEditorSidebar(null);
                                    isHover && changeUser({ side_bar: !side_bar });
                                }}
                                sx={[
                                    {
                                        cursor: side_bar || isHover ? 'pointer' : 'default',
                                        zIndex: 1000,
                                    },
                                ]}
                            >
                                <Tooltip
                                    title={<Typography children={t('expand')} color={theme => theme.custom.c22} variant="subtitle2" />}
                                    disableInteractive
                                    placement="left"
                                    sideOffset={8}
                                    contentClassName="py-2 pr-3 pl-0 bg-bg-paper"
                                    open={isHover && !side_bar}
                                >
                                    <div className="flex">
                                        <DoubleArrowIcon
                                            color="info"
                                            sx={{
                                                fontSize: '1.25rem',
                                                transition: 'all .5s',
                                                transform: side_bar ? 'rotate(180deg)' : 'rotate(0deg)',
                                            }}
                                            onMouseEnter={() => setHover(true)}
                                        />
                                    </div>
                                </Tooltip>

                                {side_bar && (
                                    <Typography
                                        children={t(side_bar ? 'collapse' : 'expand')}
                                        color={theme => (isHover ? theme.custom.c22 : 'disabled')}
                                        variant="subtitle2"
                                    />
                                )}
                            </Grid>
                            <Grid
                                container
                                sx={{
                                    gap: '8px',
                                    borderBottom: theme => `1px solid ${theme.custom.c7}`,
                                    paddingBottom: '8px',
                                    width: side_bar ? '100%' : '40px',
                                }}
                            >
                                <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].company} mode="menu">
                                    <NavigationCompany setOpen={setOpenLink} />
                                </Hint>

                                {/* left it here for now if we need to return it back */}

                                {/* <Grid container flexDirection="column" gap="2px">
                                    <NavigationLink
                                        to={paths.company.projects}
                                        link={{
                                            component: TheatersIcon,
                                            title: t('projects'),
                                            textColor: theme => (isDarkMode ? theme.palette.primary.contrastText : theme.custom.c22),
                                            iconSize: '1.25rem',
                                        }}
                                        setOpen={setOpenLink}
                                    />
                                    <NavigationLink
                                        to={paths.employees.index}
                                        link={{
                                            component: PeopleAltIcon,
                                            title: t('employees'),
                                            textColor: theme => theme.palette.primary.contrastText,
                                            iconSize: '1.25rem',
                                        }}
                                        setOpen={setOpenLink}
                                    />
                                </Grid> */}
                            </Grid>
                        </Grid>

                        <Box sx={styles.navigation} children={links} />
                    </Grid>
                </Grid>
                <div className="flex flex-wrap w-full gap-[8px] flex-col">
                    {side_bar && !!projectId && (
                        <Box
                            sx={{
                                fontSize: '0.75rem',
                                ...(currentVersion && {
                                    backgroundColor: isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                                    border: theme => `1px solid ${theme.palette.error.main}`,
                                    borderRadius: '4px',
                                    padding: '6px 8px',
                                }),
                            }}
                        >
                            {`${t('version')}: ${currentVersion?.name || t('version-current')}`}
                            {currentVersion && (
                                <>
                                    <div className="text-[0.625rem] mt-[4px]">
                                        <DateTypography date={currentVersion.created_at} />
                                    </div>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            setVersionValue('');
                                            showSnackbar({
                                                message: `${t('version')}: ${t('version-current')}`,
                                                open: true,
                                                severity: snackbarColor.success,
                                            });
                                        }}
                                        className="mt-1 text-[0.625rem]"
                                        children={t('version-to-current')}
                                    />
                                </>
                            )}
                        </Box>
                    )}
                    <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].import} mode="menu">
                        <ImportScript />
                    </Hint>

                    <ExportScript />

                    {/* Help, down here wherever the menu above doesn't carry it (Film mode moved it out of NavigationWrap) */}
                    {!menuShowsHelp && (
                        <Hint block={hintsBlock.menu} hint={HINTS[hintsBlock.menu].help} mode="menu">
                            <NavigationLink
                                testId="NavigationLinkHelp"
                                to={''}
                                link={{
                                    component: HelpOutlineIcon,
                                    title: t('help'),
                                    textColor: theme => theme.palette.primary.contrastText,
                                    iconSize: '1.25rem',
                                }}
                                isOpen={isOpenLink === t('help')}
                                setOpen={setOpenLink}
                                children={helpLinks(t, tutorialOn)}
                            />
                        </Hint>
                    )}

                    <NavigationProfile />
                </div>
            </SideBarGrid>
        </SideBarContainer>
    );
};
